import React, { useState, useContext, useEffect } from 'react';
import styled, { keyframes, css } from 'styled-components';
import * as Accordion from '@radix-ui/react-accordion';
import { Bars3Icon, XMarkIcon, ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/24/solid';
import { Modal } from 'antd';
import { findIndex, includes } from 'lodash-es';

import { AuthContext } from '@app/providers/firebase/AuthProvider';
import { useRecoilValue, useSetRecoilState } from 'recoil';

import { showMessageCenterAtom, showSettingsCenterAtom } from '@shared/state/messageState';
import { getMenuItems, MenuItem as MenuItemType, MenuCategory } from './menuItems';
import ScopedComponent from '@ui/Utility/ScopedComponent';
import { usePermissions } from '@shared/permissions/usePermissions';
import theme from '@config/theme';
import { FlexRow } from '@ui/Containers/FlexContainer';
import { clearRedirect } from '@lib/util';
import { clearCache as clearLocalCache } from '@services/db/localCache';
import useFirebase from '@app/providers/firebase';
import { signOut } from 'firebase/auth';
import { rgba } from '@config/palette/utils';
import { useHistory } from 'react-router-dom';

// Keyframes for OverlayMenu animation
const overlayOpenAnimation = keyframes`
  from {
    height: 0;
    opacity: 1;
  }
  to {
    height: calc(100vh - 48px); // Target height
    opacity: 1;
  }
`;
const overlayCloseAnimation = keyframes`
  from {
    height: calc(100vh - 48px); // Target height
    opacity: 1;
  }
  to {
    height: 0; // Target height
    opacity: 1;
  }
`;

const MobileNavWrapper = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  z-index: 100000;
  @media ${theme.device.laptopL} {
    display: none;
  }
`;

const TopBar = styled(FlexRow)`
  height: 48px;
  background-color: ${theme.palette.primary.hue}; // From image comp
  color: white;
  padding: 0 16px;
  align-items: center;
  justify-content: space-between;
  position: relative; // For z-indexing against overlay if necessary
  z-index: 1001; // Higher than overlay
`;

const LogoText = styled.span`
  font-size: 20px; // Adjusted from image comp
  font-weight: 500; // Adjusted from image comp
  letter-spacing: 0.2em; // Adjusted from image comp
  text-transform: uppercase;
`;

const HamburgerButton = styled.button`
  background: none;
  border: none;
  color: white;
  cursor: pointer;
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  svg {
    width: 30px; // Adjusted from image comp
    height: 30px;
  }
`;

const OverlayMenu = styled.div<{ isOpen: boolean; isClosing?: boolean }>`
  position: relative;
  top: 0px;
  left: 0;
  width: 100%;
  background-color: white;
  z-index: 1000;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  border-top: 1px solid ${theme.palette.neutral[300]};
  height: 0;
  opacity: 0;

  ${({ isOpen, isClosing }) => {
    if (isClosing) {
      return css`
        animation: ${overlayCloseAnimation} 0.25s cubic-bezier(.08,.69,.08,.99) forwards;
      `;
    } else if (isOpen) {
      return css`
        animation: ${overlayOpenAnimation} 0.25s cubic-bezier(.08,.69,.08,.99) forwards;
      `;
    }
    return null;
  }}
`;

const slideDown = keyframes`
  from {
    height: 0;
  }
  to {
    height: var(--radix-accordion-content-height);
  }
`;

const slideUp = keyframes`
  from {
    height: var(--radix-accordion-content-height);
  }
  to {
    height: 0;
  }
`;

const AccordionRootStyled = styled(Accordion.Root)`
  width: 100%;
`;

const AccordionItemStyled = styled(Accordion.Item)`
  border-bottom: 1px solid ${theme.palette.neutral[200]};
  &:last-child {
    border-bottom: none;
  }
`;

const AccordionHeaderStyled = styled(Accordion.Header)`
  margin: 0;
`;

const AccordionTriggerStyled = styled(Accordion.Trigger)`
  background-color: ${theme.palette.neutral.white};
  padding: 15px 20px;
  width: 100%;
  text-align: left;
  border: none;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: ${theme.palette.neutral[700]};

  svg {
    width: 20px;
    height: 20px;
    transition: transform 0.2s ease-in-out;
  }

  &[data-state='open'] svg {
    transform: rotate(180deg);
  }

  &:hover {
    background-color: ${theme.palette.neutral[50]};
  }
`;

const AccordionContentStyled = styled(Accordion.Content)`
  padding: 0;
  background-color: ${theme.palette.neutral[50]}; 
  overflow: hidden;

  &[data-state='open'] {
    animation: ${slideDown} 0.3s ease-in-out;
  }
  &[data-state='closed'] {
    animation: ${slideUp} 0.3s ease-in-out;
  }
`;

const MobileMenuItemStyled = styled.button`
  display: block;
  width: 100%;
  padding: 12px 20px 12px 35px; // Indent items
  text-align: left;
  background-color: ${(props: { isAlternate: boolean }) => props.isAlternate ? rgba(theme.palette.primary[300], 0.2) : 'transparent'};
  border: none;
  border-top: 1px solid ${theme.palette.neutral[100]};
  font-size: 15px;
  color: ${theme.palette.neutral[600]};
  cursor: pointer;
  
  &:first-child {
    border-top: none; // No top border for the first item in a group if AccordionContent has padding
  }

  &:hover {
    background-color: ${theme.palette.primary.L200};
    color: ${theme.palette.primary.D200};
  }
`;

const MobileMenuSeparator = styled.hr`
  border: 0;
  border-top: 1px solid ${theme.palette.neutral[200]};
  margin: 8px 20px 8px 35px;
`;

const MobileNavbar: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isOverlayMounted, setIsOverlayMounted] = useState(false);
  const [isOverlayClosing, setIsOverlayClosing] = useState(false);
  const [openAccordionItems, setOpenAccordionItems] = useState<string[]>([]);
  const { currentUser } = useContext(AuthContext);
  const { auth } = useFirebase();
  const history = useHistory();

  const { hasPermission } = usePermissions();
  const isInternalUser = hasPermission(['orders.view', 'schedule.view', 'router.scan']);

  const setShowMessageCenter = useSetRecoilState(showMessageCenterAtom);
  const setShowSettingsCenter = useSetRecoilState(showSettingsCenterAtom);

  const toggleMenu = () => {
    if (isMenuOpen) {
      setIsOverlayClosing(true);
      setIsMenuOpen(false);
    } else {
      setIsOverlayMounted(true);
      setIsMenuOpen(true);
      setIsOverlayClosing(false);
    }
  };

  const handleAnimationEnd = () => {
    if (isOverlayClosing) {
      setIsOverlayMounted(false);
      setIsOverlayClosing(false);
    }
  };

  const handleNavigation = (url: string) => {
    clearRedirect();
    history.push(url);
    if (isMenuOpen) {
      setIsOverlayClosing(true);
      setIsMenuOpen(false);
    }
  };

  const onLogout = () => {
    Modal.confirm({
      content: 'Log out?',
      okText: 'Log out',
      cancelText: 'Cancel',
      onOk: () => {
        signOut(auth).then(() => {
          history.push('/login');
        });
      },
    });
    if (isMenuOpen) {
      setIsOverlayClosing(true);
      setIsMenuOpen(false);
    }
  };

  const toggleMailDrawer = () => {
    setShowMessageCenter(true);
    if (isMenuOpen) {
      setIsOverlayClosing(true);
      setIsMenuOpen(false);
    }
  };

  const toggleSettingsCenter = () => {
    setShowSettingsCenter(true);
    if (isMenuOpen) {
      setIsOverlayClosing(true);
      setIsMenuOpen(false);
    }
  };
  
  const handleClearCache = () => {
    Modal.confirm({
      title: 'Clear Cache',
      content: 'This will clear the local cache and refresh the page. Continue?',
      okText: 'Clear Cache',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          await clearLocalCache();
          window.location.reload();
        } catch (error) {
          Modal.error({ title: 'Error', content: 'Failed to clear cache.' });
        }
      },
    });
    if (isMenuOpen) {
      setIsOverlayClosing(true);
      setIsMenuOpen(false);
    }
  };

  if (!currentUser) return null;

  const allMenus = getMenuItems({ handleNavigation, toggleMailDrawer, toggleSettingsCenter, onLogout, handleClearCache });

  const handleAccordionValueChange = (values: string[]) => {
    setOpenAccordionItems(values);
  };

  if (!isInternalUser) return null;

  return (
      <MobileNavWrapper>
        <TopBar>
          <LogoText>H E L M</LogoText>
          <HamburgerButton onClick={toggleMenu} aria-label={isMenuOpen ? "Close menu" : "Open menu"}>
            {isMenuOpen ? <XMarkIcon /> : <Bars3Icon />}
          </HamburgerButton>
        </TopBar>
        {isOverlayMounted && (
          <OverlayMenu 
            isOpen={isMenuOpen} 
            isClosing={isOverlayClosing} 
            onAnimationEnd={handleAnimationEnd}
          >
            <AccordionRootStyled 
              type="single" 
              value={openAccordionItems} 
              onValueChange={handleAccordionValueChange}
              collapsible
            >
              {allMenus.map((menuCategory) => {
                const visibleItems = menuCategory.items.filter(item => 
                  (item.whitelist || item.whitelist.length === 0 || (currentUser.email && includes(item.whitelist, currentUser.email)))
                );
                if (visibleItems.length === 0) return null;

                return (
                  <AccordionItemStyled 
                    key={menuCategory.trigger} 
                    value={menuCategory.trigger}
                  >
                    <AccordionHeaderStyled>
                      <AccordionTriggerStyled>
                        {menuCategory.trigger}
                        {openAccordionItems.includes(menuCategory.trigger) ? <ChevronDownIcon /> : <ChevronRightIcon />}
                      </AccordionTriggerStyled>
                    </AccordionHeaderStyled>
                    <AccordionContentStyled>
                      {visibleItems.map((item, index) => {
                        if (item.type === 'separator') {
                          return null;
                        }
                        // Item is ActionMenuItem
                        return (
                          (() => {
                            const isAlternate = findIndex(visibleItems.filter((i) => i.type === 'action'), (i) => i.label === item.label) % 2 === 1; // Determine if this should be alternate style
                            
                            return (
                              <MobileMenuItemStyled 
                                key={`${menuCategory.trigger}-${item.label}-${index}`} 
                                onClick={item.action}
                                isAlternate={isAlternate}
                              >
                                {item.label}
                              </MobileMenuItemStyled>
                            );
                          })()
                          
                        );
                      })}
                    </AccordionContentStyled>
                  </AccordionItemStyled>
                );
              })}
            </AccordionRootStyled>
          </OverlayMenu>
        )}
      </MobileNavWrapper>
  );
};

export default MobileNavbar; 